import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import InfoTile from "../../../components/common/InfoTile";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetFuelMonthGridQuery } from "../fuelApi";
import { FUEL_ENTRY_COLORS, litres, money, whole } from "../fuelFormat";
import VehicleTypeSelect from "../../masters/vehicles/components/VehicleTypeSelect";
import { vehicleTypeLabel } from "../../masters/vehicles/vehicleTypes";

// Fuel Monthly Summary (Fuel.ViewReports), 2026-09-23: "fuel summary month
// wise with month filter where it shows all days as columns ie, 31 columns +
// the type of transaction amount ie, refill, and fuel grouped with vehicle
// type and vehicle reg number".
//
// GET /fuel/reports/month-grid returns the long shape (vehicle / entry type /
// day); this page pivots it: a group per vehicle type, one line per entry type
// per vehicle, one column per day of the month, a Total column, group subtotals
// and a grand total. Amounts only in the cells (whole shillings - 31 columns
// leave no room for decimals); a Fuel cell's tooltip carries the litres and the
// number of fills. A refill on a card with no vehicle assigned sits in a
// "(no vehicle)" group under its card number, so no money disappears.
//
// Batch200 (2026-10-02): "the monthly summary should show the direct cash as
// seperate row and all in different colors. when taking report for a period the
// last closing balance should show as opening amount in the summary":
//   * three lines per vehicle - Fuel, Card refill, Cash - each in its own colour
//     (FUEL_ENTRY_COLORS, shared with the Refills list);
//   * Opening column = the vehicle's closing balance at the end of the previous
//     month (all refills + cash - fuel before this month, server-computed as the
//     OPENING row), and Closing = Opening + Refill + Cash - Fuel. Next month's
//     Opening is this month's Closing, so a month never starts from zero.

const ENTRY_ORDER = ["FUEL", "REFILL", "CASH"];
const ENTRY_LABEL = { FUEL: "Fuel", REFILL: "Card refill", CASH: "Cash" };
const NO_VEHICLE = "(no vehicle)";
const OPENING = FUEL_ENTRY_COLORS.OPENING;

const typeLabel = (t) => (t === NO_VEHICLE ? "No vehicle assigned" : vehicleTypeLabel(t) || t);
const cell = (n) => (Number(n) > 0 ? whole(n) : "");

// Closing = opening + money in (card refills + cash) - fuel bought.
const closingOf = (opening, lines) =>
  Number(opening || 0) + Number(lines.REFILL?.total || 0) + Number(lines.CASH?.total || 0) - Number(lines.FUEL?.total || 0);
const balanceStyle = (n) => ({
  textAlign: "right",
  whiteSpace: "nowrap",
  fontWeight: 700,
  verticalAlign: "middle",
  color: n > 0 ? "#1B5E20" : n < 0 ? "#B71C1C" : "#6c757d",
});
const balanceText = (n) => (n < 0 ? `-${money(-n)}` : money(n));

// Pivot the long rows into: groups[] -> vehicles[] -> { opening, lines{FUEL, REFILL, CASH} -> days[1..31] }.
const buildGrid = (rows, daysInMonth) => {
  const emptyDays = () => Array.from({ length: daysInMonth + 1 }, () => ({ amount: 0, litres: 0, count: 0 }));
  const groups = new Map();
  for (const r of rows || []) {
    const gKey = r.vehicleType || "";
    if (!groups.has(gKey)) groups.set(gKey, { vehicleType: gKey, vehicles: new Map() });
    const g = groups.get(gKey);
    const vKey = r.vehicleId ?? `card:${r.regNo}`;
    if (!g.vehicles.has(vKey)) g.vehicles.set(vKey, { key: `${gKey}|${vKey}`, regNo: r.regNo, cards: new Set(), opening: 0, lines: {} });
    const v = g.vehicles.get(vKey);
    if (r.cardNumber) v.cards.add(r.cardNumber);
    if (r.entryType === "OPENING") {
      v.opening += Number(r.amount || 0);
      continue;
    }
    if (!v.lines[r.entryType]) v.lines[r.entryType] = { days: emptyDays(), total: 0, litres: 0, count: 0 };
    const line = v.lines[r.entryType];
    const d = line.days[r.dayOfMonth];
    if (!d) continue;
    d.amount += Number(r.amount || 0);
    d.litres += Number(r.litres || 0);
    d.count += Number(r.entryCount || 0);
    line.total += Number(r.amount || 0);
    line.litres += Number(r.litres || 0);
    line.count += Number(r.entryCount || 0);
  }

  const sumVehicles = (vehicleList) => {
    const lines = {};
    for (const e of ENTRY_ORDER) {
      const days = emptyDays();
      let total = 0;
      for (const v of vehicleList) {
        const line = v.lines[e];
        if (!line) continue;
        for (let d = 1; d <= daysInMonth; d += 1) days[d].amount += line.days[d].amount;
        total += line.total;
      }
      lines[e] = { days, total };
    }
    return { opening: vehicleList.reduce((t, v) => t + v.opening, 0), lines };
  };

  const out = [...groups.values()]
    .map((g) => {
      const vehicles = [...g.vehicles.values()].sort((a, b) => a.regNo.localeCompare(b.regNo));
      return { vehicleType: g.vehicleType, vehicles, totals: sumVehicles(vehicles) };
    })
    .sort((a, b) => {
      if (a.vehicleType === NO_VEHICLE) return 1;
      if (b.vehicleType === NO_VEHICLE) return -1;
      return a.vehicleType.localeCompare(b.vehicleType);
    });
  const allVehicles = out.flatMap((g) => g.vehicles);
  return { groups: out, grand: sumVehicles(allVehicles), vehicleCount: allVehicles.length, allVehicles };
};

const FuelMonthlySummaryPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [monthValue, setMonthValue] = useState(dayjs().format("YYYY-MM"));
  const [vehicleType, setVehicleType] = useState("");

  const month = dayjs(`${monthValue}-01`);
  const daysInMonth = month.daysInMonth();
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);
  // Weekend columns get a faint darker wash so a run of
  // blank days reads as "Sunday", not "missing".
  const isWeekend = (d) => [0, 6].includes(month.date(d).day());

  const { data, isLoading, isFetching } = useGetFuelMonthGridQuery({
    year: month.year(),
    month: month.month() + 1,
    branchId: selectedBranchId ?? undefined,
    vehicleType: vehicleType || undefined,
  });
  const grid = useMemo(() => buildGrid(data, daysInMonth), [data, daysInMonth]);
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const typeLine = `Vehicle type: ${vehicleType ? vehicleTypeLabel(vehicleType) : "All"}`;
  const monthLabel = month.format("MMMM YYYY");
  const openingLabel = `Opening ${month.format("DD MMM")}`;
  const closingLabel = `Closing ${month.endOf("month").format("DD MMM")}`;
  const totalLitres = grid.allVehicles.reduce((t, v) => t + (v.lines.FUEL?.litres || 0), 0);
  const grandClosing = closingOf(grid.grand.opening, grid.grand.lines);

  // A vehicle's lines: the entry types it has this month. A vehicle that only carries an
  // opening balance (nothing this month) still gets one blank line so it is visible.
  const linesOf = (v) => {
    const present = ENTRY_ORDER.filter((e) => v.lines[e]);
    return present.length ? present : [null];
  };

  // Export: one flat row per vehicle line, grouped by vehicle type with subtotals.
  // Opening and Closing are written on the vehicle's LAST line only, so the export's
  // per-group subtotal (which sums every numeric column) counts each vehicle once.
  const exportRows = grid.groups.flatMap((g) =>
    g.vehicles.flatMap((v) => {
      const lines = linesOf(v);
      return lines.map((e, idx) => ({
        vehicleType: typeLabel(g.vehicleType),
        regNo: v.regNo,
        entry: e ? ENTRY_LABEL[e] : "-",
        days: e ? v.lines[e].days : null,
        total: e ? v.lines[e].total : 0,
        opening: idx === lines.length - 1 ? v.opening : null,
        closing: idx === lines.length - 1 ? closingOf(v.opening, v.lines) : null,
      }));
    })
  );
  const exportColumns = [
    { header: "Type", value: (r) => r.vehicleType },
    { header: "Vehicle", value: (r) => r.regNo },
    { header: "Entry", value: (r) => r.entry },
    { header: "Opening", value: (r) => (r.opening == null ? "" : balanceText(r.opening)), align: "right" },
    ...days.map((d) => ({ header: String(d), value: (r) => (r.days && r.days[d].amount > 0 ? whole(r.days[d].amount) : ""), align: "right" })),
    { header: "Total", value: (r) => (r.total > 0 ? money(r.total) : ""), align: "right" },
    { header: "Closing", value: (r) => (r.closing == null ? "" : balanceText(r.closing)), align: "right" },
  ];
  const exportTotals = {
    Type: "Total",
    Opening: balanceText(grid.grand.opening),
    ...Object.fromEntries(days.map((d) => [String(d), cell(ENTRY_ORDER.reduce((t, e) => t + grid.grand.lines[e].days[d].amount, 0))])),
    Total: money(ENTRY_ORDER.reduce((t, e) => t + grid.grand.lines[e].total, 0)),
    Closing: balanceText(grandClosing),
  };

  const LEFT_ENTRY = 120;
  const stickyStyle = (left, extra = {}) => ({ position: "sticky", left, background: "#fff", zIndex: 2, ...extra });
  const weekendWash = "linear-gradient(rgba(0,0,0,0.04), rgba(0,0,0,0.04))";
  const dayCellStyle = (d, extra = {}) => ({
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    backgroundImage: isWeekend(d) ? weekendWash : undefined,
    ...extra,
  });
  const openingCellStyle = (n, extra = {}) => ({
    ...balanceStyle(n),
    color: n === 0 ? "#6c757d" : OPENING.text,
    ...extra,
  });

  // Totals block (type subtotal / grand total): one row per entry type with money, the
  // opening and closing spanning them.
  const renderTotalsRows = (label, totals, { strong = false, background } = {}) => {
    const entries = ENTRY_ORDER.filter((e) => totals.lines[e].total > 0);
    const shown = entries.length ? entries : [null];
    const closing = closingOf(totals.opening, totals.lines);
    return shown.map((entryType, idx) => {
      const line = entryType ? totals.lines[entryType] : null;
      const c = entryType ? FUEL_ENTRY_COLORS[entryType] : null;
      return (
        <tr key={`${label}-${entryType || "none"}`} style={{ fontWeight: strong ? 700 : 600, background }}>
          {idx === 0 && (
            <td rowSpan={shown.length} style={stickyStyle(0, { background, verticalAlign: "middle" })}>{label}</td>
          )}
          <td style={stickyStyle(LEFT_ENTRY, { background, color: c?.text })} className="small">
            {entryType ? ENTRY_LABEL[entryType] : "-"}
          </td>
          {idx === 0 && (
            <td rowSpan={shown.length} style={openingCellStyle(totals.opening)} title="Closing balance brought forward from the previous month">
              {balanceText(totals.opening)}
            </td>
          )}
          {days.map((d) => (
            <td key={d} style={dayCellStyle(d, { background, color: c?.text })}>{line ? cell(line.days[d].amount) : ""}</td>
          ))}
          <td style={{ textAlign: "right", whiteSpace: "nowrap", background, color: c?.text }}>{line && line.total > 0 ? money(line.total) : ""}</td>
          {idx === 0 && (
            <td rowSpan={shown.length} style={{ ...balanceStyle(closing), background }} title="Opening + card refills + cash - fuel">
              {balanceText(closing)}
            </td>
          )}
        </tr>
      );
    });
  };

  const colCount = daysInMonth + 5;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Fuel Monthly Summary</h4>
              <h6>Every vehicle&apos;s fuel purchases, card refills and cash, day by day, with the balance brought forward</h6>
            </div>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md"><InfoTile label="Month" value={monthLabel} /></div>
          <div className="col-6 col-md"><InfoTile label="Vehicles" value={grid.vehicleCount} /></div>
          <div className="col-6 col-md">
            <InfoTile label={openingLabel}>
              <span style={{ color: OPENING.text }}>{balanceText(grid.grand.opening)}</span>
            </InfoTile>
          </div>
          <div className="col-6 col-md">
            <InfoTile label="Fuel bought">
              <span style={{ color: FUEL_ENTRY_COLORS.FUEL.text }}>{money(grid.grand.lines.FUEL.total)}</span>
              <span className="text-muted ms-1 fw-normal" style={{ fontSize: 11 }}>({litres(totalLitres)} L)</span>
            </InfoTile>
          </div>
          <div className="col-6 col-md">
            <InfoTile label="Card refills">
              <span style={{ color: FUEL_ENTRY_COLORS.REFILL.text }}>{money(grid.grand.lines.REFILL.total)}</span>
            </InfoTile>
          </div>
          <div className="col-6 col-md">
            <InfoTile label="Cash">
              <span style={{ color: FUEL_ENTRY_COLORS.CASH.text }}>{money(grid.grand.lines.CASH.total)}</span>
            </InfoTile>
          </div>
          <div className="col-6 col-md">
            <InfoTile label={closingLabel} tone={grandClosing < 0 ? "maroon" : grandClosing > 0 ? "success" : "neutral"} value={balanceText(grandClosing)} />
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <label className="small text-muted mb-0">Month</label>
                <input
                  type="month"
                  className="form-control form-control-sm"
                  style={{ width: 170, height: 34 }}
                  value={monthValue}
                  max={dayjs().format("YYYY-MM")}
                  onChange={(e) => e.target.value && setMonthValue(e.target.value)}
                />
                <VehicleTypeSelect className="form-control form-control-sm" style={{ width: 150, height: 34 }} value={vehicleType} onChange={setVehicleType} allLabel="All Types" />
                <div className="d-flex align-items-center gap-1 flex-wrap ms-2" style={{ fontSize: 11 }}>
                  {["OPENING", ...ENTRY_ORDER].map((k) => (
                    <span key={k} className="px-1" style={{ color: FUEL_ENTRY_COLORS[k].text, fontWeight: 700 }}>
                      {k === "OPENING" ? "Opening" : ENTRY_LABEL[k]}
                    </span>
                  ))}
                </div>
              </div>
              <ListExportToolbar
                title={`Fuel Monthly Summary - ${monthLabel}`}
                subtitleLines={[
                  `Branch: ${branchCode || "All"}`,
                  typeLine,
                  "Amounts per day; Fuel = purchases on the vehicle, Card refill = money loaded onto its card, Cash = cash given for its fuel. Opening = previous month's closing; Closing = Opening + Card refill + Cash - Fuel. Voided purchases and unapproved refills excluded.",
                ]}
                branchCode={branchCode}
                columns={exportColumns}
                rows={exportRows}
                totals={exportTotals}
                groupBy={(r) => r.vehicleType}
                groupTotals
                fileName={`FuelMonthlySummary_${monthValue}`}
                orientation="landscape"
              />
            </div>

            <div className="table-responsive mt-2" style={{ maxHeight: "70vh", overflow: "auto" }}>
              <table className="table table-sm table-bordered mb-0" style={{ fontSize: 12, minWidth: 1500 }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 3, background: "#fff" }}>
                  <tr>
                    <th style={stickyStyle(0, { minWidth: LEFT_ENTRY, zIndex: 4 })}>Vehicle</th>
                    <th style={stickyStyle(LEFT_ENTRY, { minWidth: 80, zIndex: 4 })}>Entry</th>
                    <th style={{ textAlign: "right", minWidth: 95, color: OPENING.text }} title="Closing balance brought forward from the previous month">
                      Opening
                    </th>
                    {days.map((d) => (
                      <th key={d} style={dayCellStyle(d, { minWidth: 46 })} title={month.date(d).format("ddd DD MMM YYYY")}>
                        {d}
                        <div className="text-muted" style={{ fontSize: 9, fontWeight: 400 }}>{month.date(d).format("dd")}</div>
                      </th>
                    ))}
                    <th style={{ textAlign: "right", minWidth: 100 }}>Total</th>
                    <th style={{ textAlign: "right", minWidth: 100 }} title="Opening + card refills + cash - fuel">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {(isLoading || isFetching) && (
                    <tr><td colSpan={colCount} className="text-center text-muted py-3">Loading...</td></tr>
                  )}
                  {!isLoading && !isFetching && grid.groups.length === 0 && (
                    <tr><td colSpan={colCount} className="text-center text-muted py-3">No fuel purchases, refills, cash or balances brought forward for {monthLabel}.</td></tr>
                  )}
                  {!isLoading && !isFetching && grid.groups.map((g) => (
                    <React.Fragment key={g.vehicleType}>
                      <tr style={{ background: "#F1E4E7" }}>
                        <td colSpan={colCount} style={{ ...stickyStyle(0, { background: "#F1E4E7", zIndex: 1 }), fontWeight: 700, color: "#6E1423" }}>
                          {typeLabel(g.vehicleType)} <span className="fw-normal text-muted">({g.vehicles.length} {g.vehicles.length === 1 ? "vehicle" : "vehicles"})</span>
                        </td>
                      </tr>
                      {g.vehicles.map((v) => {
                        const lines = linesOf(v);
                        const closing = closingOf(v.opening, v.lines);
                        return lines.map((e, idx) => {
                          const line = e ? v.lines[e] : null;
                          const c = e ? FUEL_ENTRY_COLORS[e] : null;
                          return (
                            <tr key={`${v.key}-${e || "none"}`}>
                              {idx === 0 && (
                                <td rowSpan={lines.length} style={{ ...stickyStyle(0), verticalAlign: "top", fontWeight: 600 }}>
                                  {v.regNo}
                                  {v.cards.size > 0 && (
                                    <div className="text-muted" style={{ fontSize: 10, fontWeight: 400 }}>Card {[...v.cards].join(", ")}</div>
                                  )}
                                </td>
                              )}
                              <td style={stickyStyle(LEFT_ENTRY, { color: c?.text, fontWeight: 600 })}>
                                {e ? ENTRY_LABEL[e] : <span className="text-muted fw-normal">no activity</span>}
                              </td>
                              {idx === 0 && (
                                <td rowSpan={lines.length} style={openingCellStyle(v.opening)} title={`Closing balance at the end of ${month.subtract(1, "month").format("MMMM YYYY")}`}>
                                  {balanceText(v.opening)}
                                </td>
                              )}
                              {days.map((d) => {
                                const dc = line ? line.days[d] : null;
                                const title = dc && dc.count > 0
                                  ? e === "FUEL"
                                    ? `${month.date(d).format("DD MMM")}: ${dc.count} ${dc.count === 1 ? "fill" : "fills"}, ${litres(dc.litres)} L, ${money(dc.amount)}`
                                    : `${month.date(d).format("DD MMM")}: ${dc.count} ${e === "CASH" ? "cash" : dc.count === 1 ? "refill" : "refills"}, ${money(dc.amount)}`
                                  : undefined;
                                return (
                                  <td key={d} style={dayCellStyle(d, { color: c?.text })} title={title}>{dc ? cell(dc.amount) : ""}</td>
                                );
                              })}
                              <td
                                style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 600, color: c?.text }}
                                title={line ? (e === "FUEL" ? `${line.count} fills, ${litres(line.litres)} L` : `${line.count} ${e === "CASH" ? "cash entries" : "refills"}`) : undefined}
                              >
                                {line ? money(line.total) : ""}
                              </td>
                              {idx === 0 && (
                                <td
                                  rowSpan={lines.length}
                                  style={balanceStyle(closing)}
                                  title={`Opening ${money(v.opening)} + Refill ${money(v.lines.REFILL?.total || 0)} + Cash ${money(v.lines.CASH?.total || 0)} - Fuel ${money(v.lines.FUEL?.total || 0)}`}
                                >
                                  {balanceText(closing)}
                                </td>
                              )}
                            </tr>
                          );
                        });
                      })}
                      {g.vehicles.length > 1 && renderTotalsRows(`${typeLabel(g.vehicleType)} total`, g.totals, { background: "#FAF5F6" })}
                    </React.Fragment>
                  ))}
                </tbody>
                {grid.groups.length > 0 && (
                  <tfoot>
                    {renderTotalsRows("Grand total", grid.grand, { strong: true, background: "#F8F9FA" })}
                  </tfoot>
                )}
              </table>
            </div>
            <small className="text-muted d-block mt-1">
              <b style={{ color: FUEL_ENTRY_COLORS.FUEL.text }}>Fuel</b> = purchases recorded on the vehicle (voided ones left out).{" "}
              <b style={{ color: FUEL_ENTRY_COLORS.REFILL.text }}>Card refill</b> = money loaded onto the vehicle&apos;s card, approved only, shown against the card&apos;s default vehicle.{" "}
              <b style={{ color: FUEL_ENTRY_COLORS.CASH.text }}>Cash</b> = approved cash given for the vehicle&apos;s fuel (not a Petty Cash entry).{" "}
              <b style={{ color: OPENING.text }}>Opening</b> = the previous month&apos;s closing balance; Closing = Opening + Card refill + Cash - Fuel, and becomes next month&apos;s Opening.
              Hover a cell for the fills and litres behind it. Weekend columns are shaded.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelMonthlySummaryPage;
